import { useEffect, useRef, useState } from "react";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import type {
  TeamPlayerPublic,
  TeamPublic,
  TeamPositionEntryPublic,
  CoachTeamPublic,
  CoachPublic,
  PositionDef,
  SportPositions,
  TeamPlayerRole,
} from "@dugads/shared";
import { getPositionsForSport, isReservePosition } from "@dugads/shared";
import { X, Shield, Star, MoreVertical, AlertTriangle } from "lucide-react";
import { PositionCard } from "@/components/position-card";

interface TeamRosterPanelProps {
  team: TeamPublic;
  roster: TeamPlayerPublic[];
  positionData: TeamPositionEntryPublic[];
  coaches: CoachTeamPublic[];
  schoolCoaches: CoachPublic[];
  loading: boolean;
  positionsLoading: boolean;
  saving: boolean;
  selectedPlayerId: string | null;
  highlightedPlayerId: string | null;
  onPositionClick: (jerseyNumber: number) => void;
  onRemovePlayer: (playerId: string) => void;
  onToggleCaptain: (playerId: string) => void;
  onToggleVC: (playerId: string) => void;
  onAssignCoach: (coachId: string) => void;
  onUnassignCoach: (coachId: string) => void;
  onUpdatePlayerRole?: (playerId: string, role: TeamPlayerRole) => void;
  onUpdateCoverage?: (playerId: string, secondaryPositions: number[]) => void;
}

type ViewTab = "formation" | "roster" | "coverage";

export function TeamRosterPanel({
  team,
  roster,
  positionData,
  coaches,
  schoolCoaches,
  loading,
  positionsLoading,
  saving,
  selectedPlayerId,
  highlightedPlayerId,
  onPositionClick,
  onRemovePlayer,
  onToggleCaptain,
  onToggleVC,
  onAssignCoach,
  onUnassignCoach,
  onUpdatePlayerRole,
  onUpdateCoverage,
}: TeamRosterPanelProps) {
  const [activeView, setActiveView] = useState<ViewTab>("formation");
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const highlightedRef = useRef<HTMLDivElement>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // Scroll highlighted player into view
  useEffect(() => {
    if (highlightedPlayerId && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [highlightedPlayerId]);

  // Close action menu on outside click
  useEffect(() => {
    if (!actionMenuId) return;
    function handleClick(e: MouseEvent) {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setActionMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [actionMenuId]);

  const sportPositions = team.sport_name ? getPositionsForSport(team.sport_name) : undefined;

  // Build formation rows
  let sortedRows: [number, { pos: PositionDef }[]][] = [];
  if (sportPositions) {
    const rows = new Map<number, { pos: PositionDef }[]>();
    for (const pos of sportPositions.positions) {
      const arr = rows.get(pos.row) ?? [];
      arr.push({ pos });
      rows.set(pos.row, arr);
    }
    for (const arr of rows.values()) {
      arr.sort((a, b) => a.pos.col - b.pos.col);
    }
    sortedRows = [...rows.entries()].sort((a, b) => a[0] - b[0]);
  }

  // Find the active player's position entry (if selected player has a position)
  const activeEntry = selectedPlayerId
    ? positionData.find((p) => p.player_id === selectedPlayerId) ?? null
    : null;

  function getPlayersForPosition(jerseyNumber: number) {
    return positionData
      .filter(
        (p) =>
          p.jersey_number === jerseyNumber ||
          p.covers_positions.includes(jerseyNumber),
      )
      .map((p) => ({
        entry: p,
        isPrimary: p.jersey_number === jerseyNumber,
      }));
  }

  function getPlayerAtPosition(jerseyNumber: number) {
    return positionData.find((p) => p.jersey_number === jerseyNumber);
  }

  const isTrial = team.team_type === "trial";

  return (
    <div
      className={`flex-1 min-w-0 rounded-lg border p-3 transition-colors ${
        isTrial ? "border-dashed border-trial/50 border-border" : "border-border"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">{team.name}</span>
          {isTrial && (
            <span className="rounded border border-dashed border-trial bg-trial/10 px-1.5 py-0.5 text-[10px] font-semibold text-trial">
              Trial
            </span>
          )}
          <span className="text-sm text-muted-foreground">
            {roster.length} player{roster.length !== 1 ? "s" : ""}
          </span>
          {team.season_year && (
            <span className="text-xs text-muted-foreground">
              Season {team.season_year}
            </span>
          )}
        </div>
        {selectedPlayerId && (
          <span className="text-xs text-primary animate-pulse">
            Click a position to assign
          </span>
        )}
      </div>

      {/* Coach / Captain / VC info bar */}
      <TeamInfoBar
        coaches={coaches}
        schoolCoaches={schoolCoaches}
        positionData={positionData}
        roster={roster}
        saving={saving}
        onAssignCoach={onAssignCoach}
        onUnassignCoach={onUnassignCoach}
      />

      {/* View tabs -- only show when sport has positions */}
      {sportPositions && (
        <div className="flex gap-1 mb-3">
          <button
            type="button"
            onClick={() => setActiveView("formation")}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              activeView === "formation"
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            Formation
          </button>
          {onUpdateCoverage && (
            <button
              type="button"
              onClick={() => setActiveView("coverage")}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                activeView === "coverage"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              Coverage
            </button>
          )}
          <button
            type="button"
            onClick={() => setActiveView("roster")}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              activeView === "roster"
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            Roster ({roster.length})
          </button>
        </div>
      )}

      {loading && (
        <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
      )}

      {/* Formation grid */}
      {!loading && sportPositions && activeView === "formation" && (
        <div className="rounded-lg border border-border">
          {positionsLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading formation...</p>
          ) : (
            <>
              {/* Starters -- field area */}
              <div className="flex flex-col items-center gap-3 p-4 bg-green-900/10 dark:bg-green-900/20 rounded-t-lg">
                {sortedRows
                  .filter(([, rowItems]) => rowItems[0]?.pos.group !== "reserves")
                  .map(([rowIdx, rowItems]) => (
                    <div key={rowIdx} className="flex flex-wrap justify-center gap-2">
                      {rowItems.map(({ pos }) => (
                        <PositionSlot key={pos.number} jersey={pos.number}>
                          <PositionCard
                            jerseyNumber={pos.number}
                            position={pos}
                            holder={getPlayerAtPosition(pos.number)}
                            playersForPosition={getPlayersForPosition(pos.number)}
                            activeEntry={activeEntry}
                            activePlayerId={selectedPlayerId}
                            highlightedPlayerId={highlightedPlayerId}
                            canEdit={true}
                            saving={saving}
                            onClick={() => onPositionClick(pos.number)}
                            onToggleCaptain={onToggleCaptain}
                            onToggleVC={onToggleVC}
                            onRemovePlayer={onRemovePlayer}
                          />
                        </PositionSlot>
                      ))}
                    </div>
                  ))}
              </div>

              {/* Bench divider + reserves */}
              {sortedRows.some(([, rowItems]) => rowItems[0]?.pos.group === "reserves") && (
                <>
                  <div className="flex items-center gap-2 px-4 py-1.5 bg-muted/50 border-y border-border">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bench</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 p-4 bg-muted/20 rounded-b-lg">
                    {sortedRows
                      .filter(([, rowItems]) => rowItems[0]?.pos.group === "reserves")
                      .flatMap(([, rowItems]) => rowItems)
                      .map(({ pos }) => (
                        <PositionSlot key={pos.number} jersey={pos.number}>
                          <PositionCard
                            jerseyNumber={pos.number}
                            position={pos}
                            holder={getPlayerAtPosition(pos.number)}
                            playersForPosition={getPlayersForPosition(pos.number)}
                            activeEntry={activeEntry}
                            activePlayerId={selectedPlayerId}
                            highlightedPlayerId={highlightedPlayerId}
                            canEdit={true}
                            saving={saving}
                            onClick={() => onPositionClick(pos.number)}
                            onToggleCaptain={onToggleCaptain}
                            onToggleVC={onToggleVC}
                            onRemovePlayer={onRemovePlayer}
                          />
                        </PositionSlot>
                      ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Roster list -- simplified per 037 */}
      {!loading && (activeView === "roster" || !sportPositions) && (
        <>
          {roster.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center border border-dashed border-border rounded-md">
              <p className="text-sm text-muted-foreground">No players assigned yet</p>
              <p className="text-xs text-muted-foreground">
                Click a player from the pool or drag them here
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {[...roster].sort((a, b) => {
                const posA = positionData.find((p) => p.player_id === a.player_id);
                const posB = positionData.find((p) => p.player_id === b.player_id);
                const numA = posA?.jersey_number ?? Infinity;
                const numB = posB?.jersey_number ?? Infinity;
                return numA - numB;
              }).map((tp) => {
                const posEntry = positionData.find((p) => p.player_id === tp.player_id);
                const isHL = highlightedPlayerId === tp.player_id;
                const showActionMenu = actionMenuId === tp.player_id;
                return (
                  <div
                    key={tp.id}
                    ref={isHL ? highlightedRef : undefined}
                    className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                      isHL
                        ? "bg-primary/10 ring-1 ring-primary/30"
                        : "hover:bg-muted/30"
                    }`}
                  >
                    {posEntry && (
                      <span className="text-xs font-bold text-muted-foreground w-5 text-center shrink-0">
                        {posEntry.jersey_number}
                      </span>
                    )}
                    <span className="flex-1 truncate">
                      {tp.player_name}
                      {tp.nickname && (
                        <span className="text-muted-foreground ml-1">({tp.nickname})</span>
                      )}
                    </span>
                    {/* Indemnity flag */}
                    {!tp.indemnity_complete && (
                      <span className="shrink-0" title="Indemnity incomplete">
                        <AlertTriangle className="h-3.5 w-3.5 text-trial" />
                      </span>
                    )}
                    {/* Reserve badge */}
                    {posEntry && sportPositions && isReservePosition(sportPositions, posEntry.jersey_number) && (
                      <span className="rounded bg-orange-500/80 px-1 py-px text-[9px] font-bold text-white shrink-0">RES</span>
                    )}
                    {/* Standing role badges from TeamPlayer.role (037) */}
                    {tp.role === "captain" && (
                      <span className="rounded bg-amber-500 px-1 py-px text-[9px] font-bold text-white shrink-0">C</span>
                    )}
                    {tp.role === "vice_captain" && (
                      <span className="rounded bg-blue-500 px-1 py-px text-[9px] font-bold text-white shrink-0">VC</span>
                    )}
                    {/* Position-level captain/VC badges (legacy from TeamPosition) */}
                    {posEntry?.is_captain && tp.role !== "captain" && (
                      <span className="rounded bg-amber-500/50 px-1 py-px text-[9px] font-bold text-white shrink-0" title="Position captain">C</span>
                    )}
                    {posEntry?.is_vice_captain && tp.role !== "vice_captain" && (
                      <span className="rounded bg-blue-500/50 px-1 py-px text-[9px] font-bold text-white shrink-0" title="Position VC">VC</span>
                    )}
                    {/* Action menu for role management */}
                    {onUpdatePlayerRole && (
                      <div className="relative shrink-0" ref={showActionMenu ? actionMenuRef : undefined}>
                        <button
                          type="button"
                          onClick={() => setActionMenuId(showActionMenu ? null : tp.player_id)}
                          disabled={saving}
                          className="rounded p-0.5 hover:bg-muted transition-colors"
                          title="Player actions"
                        >
                          <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                        {showActionMenu && (
                          <div className="absolute right-0 top-full mt-1 z-20 w-44 rounded-md border border-border bg-popover shadow-md py-1">
                            <button
                              type="button"
                              onClick={() => {
                                onUpdatePlayerRole(tp.player_id, tp.role === "captain" ? "player" : "captain");
                                setActionMenuId(null);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted transition-colors"
                            >
                              <Shield className="h-3.5 w-3.5" />
                              {tp.role === "captain" ? "Remove Captain" : "Set as Captain"}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                onUpdatePlayerRole(tp.player_id, tp.role === "vice_captain" ? "player" : "vice_captain");
                                setActionMenuId(null);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted transition-colors"
                            >
                              <Star className="h-3.5 w-3.5" />
                              {tp.role === "vice_captain" ? "Remove Vice-Captain" : "Set as Vice-Captain"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Position-level captain/VC toggle buttons (keep for formation management) */}
                    {posEntry && !onUpdatePlayerRole && (
                      <div className="flex gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => onToggleCaptain(tp.player_id)}
                          disabled={saving}
                          className={`rounded p-0.5 transition-colors ${
                            posEntry.is_captain
                              ? "bg-amber-500/20 text-amber-600"
                              : "hover:bg-muted text-muted-foreground"
                          }`}
                          title={posEntry.is_captain ? "Remove captain" : "Make captain"}
                        >
                          <Shield className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onToggleVC(tp.player_id)}
                          disabled={saving}
                          className={`rounded p-0.5 transition-colors ${
                            posEntry.is_vice_captain
                              ? "bg-blue-500/20 text-blue-600"
                              : "hover:bg-muted text-muted-foreground"
                          }`}
                          title={posEntry.is_vice_captain ? "Remove vice-captain" : "Make vice-captain"}
                        >
                          <Star className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => onRemovePlayer(tp.player_id)}
                      disabled={saving}
                      className="rounded p-0.5 hover:bg-destructive/20 shrink-0"
                      title="Remove from team"
                    >
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Coverage view -- reserve coverage planning */}
      {!loading && sportPositions && activeView === "coverage" && onUpdateCoverage && (
        <ReserveCoverageView
          sportPositions={sportPositions}
          positionData={positionData}
          saving={saving}
          onUpdateCoverage={onUpdateCoverage}
        />
      )}
    </div>
  );
}

// --- Reserve Coverage Planning View ---

function ReserveCoverageView({
  sportPositions,
  positionData,
  saving,
  onUpdateCoverage,
}: {
  sportPositions: SportPositions;
  positionData: TeamPositionEntryPublic[];
  saving: boolean;
  onUpdateCoverage: (playerId: string, secondaryPositions: number[]) => void;
}) {
  const reserves = positionData.filter((p) => isReservePosition(sportPositions, p.jersey_number));

  // Build formation rows (same logic as main formation view, starters only)
  const rows = new Map<number, { pos: PositionDef }[]>();
  for (const pos of sportPositions.positions) {
    if (pos.group === "reserves") continue;
    const arr = rows.get(pos.row) ?? [];
    arr.push({ pos });
    rows.set(pos.row, arr);
  }
  for (const arr of rows.values()) {
    arr.sort((a, b) => a.pos.col - b.pos.col);
  }
  const sortedRows = [...rows.entries()].sort((a, b) => a[0] - b[0]);

  // Build coverage map: position number → reserve entries that cover it
  const coverageMap = new Map<number, TeamPositionEntryPublic[]>();
  for (const pos of sportPositions.positions) {
    if (pos.group !== "reserves") coverageMap.set(pos.number, []);
  }
  for (const reserve of reserves) {
    for (const coveredPos of reserve.covers_positions) {
      const arr = coverageMap.get(coveredPos);
      if (arr) arr.push(reserve);
    }
  }

  function handleToggleCoverage(reservePlayerId: string, posNumber: number) {
    if (saving) return;
    const reserve = reserves.find((r) => r.player_id === reservePlayerId);
    if (!reserve) return;

    const current = reserve.covers_positions;
    const updated = current.includes(posNumber)
      ? current.filter((n) => n !== posNumber)
      : [...current, posNumber];
    onUpdateCoverage(reservePlayerId, updated);
  }

  return (
    <div className="flex gap-3">
      {/* Formation grid -- starters only, with coverage coloring */}
      <div className="flex-1 min-w-0 rounded-lg border border-border">
        <div className="flex flex-col items-center gap-3 p-4 bg-green-900/10 dark:bg-green-900/20 rounded-lg">
          {sortedRows.map(([rowIdx, rowItems]) => (
            <div key={rowIdx} className="flex flex-wrap justify-center gap-2">
              {rowItems.map(({ pos }) => {
                const coveringReserves = coverageMap.get(pos.number) ?? [];
                const holder = positionData.find((p) => p.jersey_number === pos.number);
                const covered = coveringReserves.length > 0;
                const surname = holder?.player_name.split(" ").pop()?.toUpperCase() ?? "";

                return (
                  <CoverageStarterDroppable key={pos.number} posNumber={pos.number} covered={covered}>
                    {/* Jersey + position label */}
                    <div className="flex items-center gap-1">
                      <span className="text-base font-bold">{pos.number}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">{pos.abbreviation}</span>

                    {/* Starter name */}
                    {holder ? (
                      <span className="text-[11px] font-bold truncate max-w-full leading-tight">{surname}</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic">Empty</span>
                    )}

                    {/* Reserve coverage badges */}
                    {coveringReserves.length > 0 && (
                      <div className="flex flex-wrap gap-0.5 mt-0.5 justify-center">
                        {coveringReserves.map((reserve) => {
                          const resSurname = reserve.player_name.split(" ").pop()?.toUpperCase() ?? "";
                          return (
                            <button
                              key={reserve.player_id}
                              type="button"
                              onClick={() => handleToggleCoverage(reserve.player_id, pos.number)}
                              disabled={saving}
                              className="rounded bg-green-600/80 px-1 py-px text-[9px] font-bold text-white hover:bg-destructive/80 transition-colors"
                              title={`${reserve.player_name} (#${reserve.jersey_number}) -- click to remove`}
                            >
                              #{reserve.jersey_number} {resSurname}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </CoverageStarterDroppable>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Reserves sidebar */}
      <div className="w-52 shrink-0 border-l border-border pl-3">
        <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Reserves</div>
        <div className="flex flex-col gap-1.5">
          {reserves.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              Drag players from the pool onto a position to add as reserve
            </p>
          )}
          {reserves.map((reserve) => {
            const surname = reserve.player_name.split(" ").pop()?.toUpperCase() ?? "";
            const covCount = reserve.covers_positions.length;
            return (
              <CoverageReserveDraggable key={reserve.player_id} playerId={reserve.player_id} label={surname} saving={saving}>
                <div className="flex items-center gap-1.5">
                  <span className="rounded bg-orange-500/80 px-1 py-px text-[9px] font-bold text-white shrink-0">
                    {reserve.jersey_number}
                  </span>
                  <span className="text-xs font-medium truncate flex-1">{surname}</span>
                  <span className={`text-[9px] font-bold shrink-0 ${covCount > 0 ? "text-green-600" : "text-red-500"}`}>
                    {covCount > 0 ? `${covCount} pos` : "none"}
                  </span>
                </div>
                {reserve.covers_positions.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {[...reserve.covers_positions].sort((a, b) => a - b).map((posNum) => {
                      const posDef = sportPositions.positions.find((p) => p.number === posNum);
                      return (
                        <button
                          key={posNum}
                          type="button"
                          onClick={() => handleToggleCoverage(reserve.player_id, posNum)}
                          disabled={saving}
                          className="rounded bg-green-600/80 px-1 py-px text-[9px] font-bold text-white hover:bg-destructive/80 transition-colors"
                          title={`Covers #${posNum} ${posDef?.name ?? ""} -- click to remove`}
                        >
                          #{posNum} {posDef?.abbreviation ?? ""}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CoverageReserveDraggable>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// 184: thin dnd-kit wrappers for the coverage view. The drop routing
// (toggle-coverage / assign-reserve) is handled centrally in
// inline-team-builder's onDragEnd; these just register the targets/sources.
function CoverageStarterDroppable({
  posNumber,
  covered,
  children,
}: {
  posNumber: number;
  covered: boolean;
  children: React.ReactNode;
}) {
  const drop = useDroppable({ id: `coverage-starter-${posNumber}`, data: { kind: "coverage-starter", posNumber } });
  return (
    <div
      ref={drop.setNodeRef}
      className={`flex w-[7.5rem] flex-col items-center gap-0.5 rounded-lg border-2 px-2 py-1.5 text-center transition-colors shadow-sm ${
        drop.isOver
          ? "border-primary ring-2 ring-primary/30 bg-primary/10"
          : covered
            ? "border-green-500/60 bg-green-500/10"
            : "border-red-500/50 bg-red-500/10"
      }`}
    >
      {children}
    </div>
  );
}

function CoverageReserveDraggable({
  playerId,
  label,
  saving,
  children,
}: {
  playerId: string;
  label: string;
  saving: boolean;
  children: React.ReactNode;
}) {
  const drag = useDraggable({
    id: `coverage-player-${playerId}`,
    data: { kind: "coverage-player", playerId, label },
    disabled: saving,
  });
  return (
    <div
      ref={drag.setNodeRef}
      {...drag.listeners}
      {...drag.attributes}
      className={`rounded-md border border-border px-2 py-1.5 cursor-grab hover:shadow-sm transition-shadow bg-background ${
        drag.isDragging ? "opacity-40" : ""
      }`}
    >
      {children}
    </div>
  );
}

// 184: a formation position slot is a dnd-kit drop target. The drop routing
// (assign / move / swap) is handled centrally in inline-team-builder's onDragEnd.
function PositionSlot({ jersey, children }: { jersey: number; children: React.ReactNode }) {
  const drop = useDroppable({ id: `position-${jersey}`, data: { kind: "position", jersey } });
  return (
    <div
      ref={drop.setNodeRef}
      className={`rounded-lg transition-colors ${drop.isOver ? "ring-2 ring-primary bg-primary/10" : ""}`}
    >
      {children}
    </div>
  );
}

// --- Team Info Bar ---

function TeamInfoBar({
  coaches,
  schoolCoaches,
  positionData,
  roster,
  saving,
  onAssignCoach,
  onUnassignCoach,
}: {
  coaches: CoachTeamPublic[];
  schoolCoaches: CoachPublic[];
  positionData: TeamPositionEntryPublic[];
  roster: TeamPlayerPublic[];
  saving: boolean;
  onAssignCoach: (coachId: string) => void;
  onUnassignCoach: (coachId: string) => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Standing roles from TeamPlayer.role (037) take precedence
  const standingCaptain = roster.find((tp) => tp.role === "captain");
  const standingVC = roster.find((tp) => tp.role === "vice_captain");
  // Fallback to position-level captain/VC
  const posCaptain = !standingCaptain ? positionData.find((p) => p.is_captain) : null;
  const posVC = !standingVC ? positionData.find((p) => p.is_vice_captain) : null;

  const assignedCoachIds = new Set(coaches.map((c) => c.coach_id));
  const availableCoaches = schoolCoaches.filter((c) => !assignedCoachIds.has(c.id));

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-3 text-xs text-muted-foreground">
      {/* Coach selector */}
      <div className="flex items-center gap-1.5 relative" ref={dropdownRef}>
        <span className="font-medium text-foreground shrink-0">Coach:</span>
        {coaches.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1">
            {coaches.map((c) => (
              <span
                key={c.coach_id}
                className="inline-flex items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5"
              >
                {c.user_name || c.user_email}
                <button
                  type="button"
                  onClick={() => onUnassignCoach(c.coach_id)}
                  disabled={saving}
                  className="ml-0.5 rounded hover:bg-destructive/20 p-px"
                  title="Remove coach"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="italic">None</span>
        )}
        {availableCoaches.length > 0 && (
          <button
            type="button"
            onClick={() => setDropdownOpen((v) => !v)}
            disabled={saving}
            className="rounded-md border border-dashed border-border px-1.5 py-0.5 hover:bg-muted transition-colors"
          >
            + Add
          </button>
        )}
        {dropdownOpen && availableCoaches.length > 0 && (
          <div className="absolute top-full left-0 mt-1 z-20 w-56 rounded-md border border-border bg-popover shadow-md">
            {availableCoaches.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onAssignCoach(c.id);
                  setDropdownOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted transition-colors"
              >
                <span className="truncate">{c.user_name || c.user_email}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Captain / VC display -- standing roles (037) take priority */}
      {standingCaptain && (
        <span className="flex items-center gap-1">
          <span className="rounded bg-amber-500 px-1 py-px text-[9px] font-bold text-white">C</span>
          {standingCaptain.player_name}
        </span>
      )}
      {posCaptain && (
        <span className="flex items-center gap-1">
          <span className="rounded bg-amber-500/50 px-1 py-px text-[9px] font-bold text-white">C</span>
          {posCaptain.player_name}
        </span>
      )}
      {standingVC && (
        <span className="flex items-center gap-1">
          <span className="rounded bg-blue-500 px-1 py-px text-[9px] font-bold text-white">VC</span>
          {standingVC.player_name}
        </span>
      )}
      {posVC && (
        <span className="flex items-center gap-1">
          <span className="rounded bg-blue-500/50 px-1 py-px text-[9px] font-bold text-white">VC</span>
          {posVC.player_name}
        </span>
      )}
    </div>
  );
}
